import * as RadixDialog from '@radix-ui/react-dialog'
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'
import { DsSpinner } from '@/ds/spinner'
import { DsKbd } from '@/ds/tooltip'

export interface DsCommandItem {
  id: string
  label: string
  description?: string
  icon?: DsIcon
  /** Extra words that should match (e.g. "payments" for Transactions). */
  keywords?: string[]
  hint?: ReactNode
  onSelect: () => void
}

export interface DsCommandGroup {
  label: string
  items: DsCommandItem[]
  /** Items come from a server search: shown as given, not filtered again. */
  remote?: boolean
  loading?: boolean
}

export interface DsCommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groups: DsCommandGroup[]
  /** Controlled query so the owner can run server searches for remote groups. */
  query: string
  onQueryChange: (query: string) => void
  placeholder?: string
  emptyLabel?: string
}

export function commandMatches(item: Pick<DsCommandItem, 'label' | 'description' | 'keywords'>, query: string): boolean {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return true
  const hay = [item.label, item.description ?? '', ...(item.keywords ?? [])].join(' ').toLowerCase()
  return tokens.every((t) => hay.includes(t))
}

// `remote` groups are results the owner fetched for the same query; local groups filter as you type.
export function DsCommandPalette({ open, onOpenChange, groups, query, onQueryChange, placeholder = 'Search or jump to…', emptyLabel = 'No matches' }: DsCommandPaletteProps) {
  const listId = useId()
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const visible = useMemo(
    () => groups.map((g) => ({ ...g, items: g.remote ? g.items : g.items.filter((i) => commandMatches(i, query)) })).filter((g) => g.items.length > 0 || g.loading),
    [groups, query],
  )
  const flat = useMemo(() => visible.flatMap((g) => g.items), [visible])

  useEffect(() => setActive(0), [query, open])
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' })
  }, [active])

  const run = (item: DsCommandItem | undefined) => {
    if (!item) return
    onOpenChange(false)
    item.onSelect()
  }
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); run(flat[active]) }
  }

  let index = -1
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim animate-ds-fade-in" />
        <RadixDialog.Content className="fixed left-1/2 top-[12vh] z-50 flex max-h-[70vh] w-[min(640px,calc(100vw-32px))] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-lg outline-none animate-ds-scale-in">
          <RadixDialog.Title className="sr-only">Command menu</RadixDialog.Title>
          <RadixDialog.Description className="sr-only">Search pages, actions and records</RadixDialog.Description>
          <div className="flex items-center gap-3 border-b px-4">
            <DsIconGlyph icon={DsIcons.search} size={18} className="text-muted-foreground" />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={flat[active] ? `${listId}-${active}` : undefined}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="h-14 flex-1 bg-transparent text-body text-foreground outline-none placeholder:text-muted-foreground"
            />
            <DsKbd>Esc</DsKbd>
          </div>
          <div ref={listRef} id={listId} role="listbox" aria-label="Results" className="flex-1 overflow-y-auto p-2">
            {flat.length === 0 && !visible.some((g) => g.loading) && <p className="px-3 py-8 text-center text-small text-muted-foreground">{emptyLabel}</p>}
            {visible.map((g) => (
              <div key={g.label} role="group" aria-label={g.label} className="mb-1">
                <div className="flex items-center gap-2 px-3 pb-1 pt-2 text-caption uppercase tracking-wide text-muted-foreground">
                  {g.label}
                  {g.loading && <DsSpinner size={12} />}
                </div>
                {g.items.map((item) => {
                  index += 1
                  const i = index
                  return (
                    <div
                      key={item.id}
                      id={`${listId}-${i}`}
                      data-index={i}
                      role="option"
                      aria-selected={i === active}
                      onMouseMove={() => setActive(i)}
                      onClick={() => run(item)}
                      className={cn('flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2', i === active ? 'bg-accent text-accent-foreground' : 'text-foreground')}
                    >
                      {item.icon && <DsIconGlyph icon={item.icon} size={18} className="shrink-0 opacity-80" />}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-body">{item.label}</div>
                        {item.description && <div className="truncate text-caption text-muted-foreground">{item.description}</div>}
                      </div>
                      {item.hint && <div className="shrink-0 text-caption text-muted-foreground">{item.hint}</div>}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
